import React, { useEffect, useRef, useState } from 'react';
import { Upload } from 'antd';
import { message } from '../services/toastMessage';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { uploadVideos, MediaDomain } from '../services/MediaService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const DEFAULT_ALLOWED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];

export interface VideoUploadProps {
  value?: string[];
  onChange?: (videos: string[]) => void;
  /** Which Blob Storage folder new uploads land in — see MediaDomain. */
  domain: MediaDomain;
  maxCount?: number;
  maxSizeMB?: number;
  allowedTypes?: string[];
  invalidTypeMessage?: string;
  tooLargeMessage?: string;
  uploadFailedMessage?: string;
  disabled?: boolean;
  buttonLabel?: string;
}

/**
 * Video counterpart to PhotoUpload — same value/onChange contract, no
 * thumbnail generation (HeavyDutyMachineryApi doesn't create a poster
 * frame yet, so the plain file-name list view is used instead of
 * picture-card). Uploads via HeavyDutyMachineryApi's MediaController.
 */
const VideoUpload: React.FC<VideoUploadProps> = ({
  value,
  onChange,
  domain,
  maxCount = 3,
  maxSizeMB = 200,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  invalidTypeMessage,
  tooLargeMessage,
  uploadFailedMessage,
  disabled,
  buttonLabel,
}) => {
  const { t } = useTranslation();
  const resolvedButtonLabel = buttonLabel ?? translate(t, TRANSLATIONS.VIDEO_UPLOAD_BUTTON_LABEL);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current || !value || value.length === 0) return;
    initializedRef.current = true;
    setFileList(
      value.map((url, index) => ({
        uid: `existing-${index}`,
        name: url.split('/').pop() || `video-${index + 1}`,
        status: 'done',
        url,
      }))
    );
  }, [value]);

  const beforeUpload = (file: RcFile) => {
    if (!allowedTypes.includes(file.type)) {
      message.error(invalidTypeMessage || translate(t, TRANSLATIONS.VIDEO_UPLOAD_INVALID_TYPE));
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 >= maxSizeMB) {
      message.error(tooLargeMessage || t(TRANSLATIONS.VIDEO_UPLOAD_TOO_LARGE, { maxSizeMB }));
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const computeVideos = async (list: UploadFile[]): Promise<string[]> => {
    const newFiles = list.filter((file) => file.originFileObj);
    let uploaded: { url: string }[] = [];

    if (newFiles.length > 0) {
      try {
        uploaded = await uploadVideos(domain, newFiles.map((file) => file.originFileObj as File));
      } catch {
        message.error(uploadFailedMessage || translate(t, TRANSLATIONS.VIDEO_UPLOAD_FAILED));
      }
    }

    let uploadIndex = 0;
    const videos: string[] = [];
    for (const file of list) {
      if (file.originFileObj) {
        const result = uploaded[uploadIndex++];
        if (result) videos.push(result.url);
      } else if (file.url) {
        videos.push(file.url);
      }
    }
    return videos;
  };

  const handleChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
    const limited = newFileList.slice(0, maxCount);
    setFileList(limited);
    const videos = await computeVideos(limited);
    onChange?.(videos);
  };

  return (
    <Upload
      listType="text"
      fileList={fileList}
      beforeUpload={beforeUpload}
      onChange={handleChange}
      maxCount={maxCount}
      multiple
      accept={allowedTypes.join(',')}
      disabled={disabled}
    >
      {fileList.length < maxCount && (
        <button type="button" style={{ border: 0, background: 'none', cursor: 'pointer' }} disabled={disabled}>
          <UploadOutlined /> {resolvedButtonLabel}
        </button>
      )}
    </Upload>
  );
};

export default VideoUpload;
